import React, { useMemo } from 'react';
import * as THREE from 'three';
import { RolledSteelChannelSection } from '../../types/beamSection';

/**
 * Rolled Steel Channel Section Component
 * Renders an engineering-accurate C-Channel cross-section along the beam
 * 
 * CHANNEL GEOMETRY (C-shaped profile):
 * 
 *        ← b (Width) →
 *        ┌─────────────┐  ↑
 *        │   tf        │  │
 *        ├──┐          │  │
 *        │  │          │  │ h (Depth)
 *        │  │ tw       │  │
 *        │  │          │  │
 *        ├──┘          │  │
 *        │             │  │
 *        └─────────────┘  ↓
 *        
 *        Back of web ↑
 *                    └─→ cyy (centroid offset)
 * 
 * IMPORTANT: Matches CylinderGeometry and BoxGeometry behavior:
 * - Cross-section is in XZ plane
 * - Extrusion is along Y-axis
 * - Parent group applies quaternion rotation to align with beam direction
 * 
 * This ensures proper rendering for horizontal, vertical, and diagonal beams.
 */
interface RolledSteelChannelMeshProps {
  section: RolledSteelChannelSection;
  color: string;
}

export const RolledSteelChannelMesh: React.FC<RolledSteelChannelMeshProps> = ({ section, color }) => {
  const { h, b, tf, tw, length } = section;
  
  // Convert mm to meters for Three.js (assuming beam units are in meters)
  const scale = 0.02; // 1mm = 0.001m
  
  const height = h * scale;
  const width = b * scale;
  const flangeThickness = tf * scale;
  const webThickness = tw * scale;
  
  /**
   * Create C-Channel cross-section shape
   * 
   * IMPORTANT: THREE.Shape draws in XY plane, ExtrudeGeometry extrudes along Z
   * But CylinderGeometry has its axis along Y
   * 
   * Solution:
   * 1. Draw C-channel shape in XY plane (X = width, Y = height)
   * 2. Extrude along Z for beam length
   * 3. Rotate -90° around X-axis to make Z-axis align with Y-axis
   * 4. Result: Extrusion is now along Y-axis (matches CylinderGeometry)
   */
  const channelGeometry = useMemo(() => {
    const shape = new THREE.Shape();
    
    // Draw C-channel cross-section in XY plane
    // X-axis = width (left-right)
    // Y-axis = height (up-down)
    const halfHeight = height / 2;
    
    // Start from bottom-left corner (back of web)
    // Draw clockwise to create the C-shape
    
    // Bottom flange (full width)
    shape.moveTo(0, -halfHeight);  // Bottom-left (back of web)
    shape.lineTo(width, -halfHeight);  // Bottom-right (toe of bottom flange)
    shape.lineTo(width, -halfHeight + flangeThickness);  // Up to top of bottom flange
    
    // Right edge (inside of top flange)
    shape.lineTo(webThickness, -halfHeight + flangeThickness);  // Back to web
    shape.lineTo(webThickness, halfHeight - flangeThickness);  // Up along web
    
    // Top flange (full width)
    shape.lineTo(width, halfHeight - flangeThickness);  // Out to inside of top flange
    shape.lineTo(width, halfHeight);  // Up to top edge
    shape.lineTo(0, halfHeight);  // Across to back of web
    
    // Left edge (back of web)
    shape.lineTo(0, -halfHeight);  // Down back of web to start
    
    // Extrude along Z-axis (default for ExtrudeGeometry)
    // IMPORTANT: length is already in correct units (meters), don't scale it
    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: length,  // Extrudes along Z from 0 to +length
      bevelEnabled: false,
      steps: 1,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    // Now geometry is:
      // - Cross-section in XY plane
      // - Extruded along Z from 0 to +length

      // We need to:
      // 1. Rotate so extrusion is along Y-axis (like CylinderGeometry)
      // 2. Center along the extrusion axis

      // Rotate -90° around X-axis: converts Z-extrusion to Y-extrusion
      geometry.rotateX(-Math.PI / 2);

      // After rotation:
      // - Cross-section is now in XZ plane
      // - Extrusion is now along Y-axis from 0 to +length

      // Center on Y-axis (same as CylinderGeometry)
    geometry.translate(0, -length / 2, 0);

    // Center horizontally (align back of web with origin)
    geometry.translate(-width / 2, 0, 0);
    
    // Final result:
    // - Cross-section in XZ plane
    // - Extruded along Y from -length/2 to +length/2
    // - Centered horizontally
    // - Matches CylinderGeometry behavior exactly!
    
    return geometry;
  }, [height, width, flangeThickness, webThickness, length]);

  // Debug logging
  console.log('🔧 RolledSteelChannelMesh:', {
    designation: section.designation,
    length,
    dimensions: `${h}mm × ${b}mm (h × b)`,
    'scaled': `${height.toFixed(3)}m × ${width.toFixed(3)}m`,
    'extrusion axis': 'Y-axis (after rotation)',
    'geometry span': `Y: -${(length/2).toFixed(2)} to +${(length/2).toFixed(2)}`,
    'profile': 'C-Channel'
  });

  return (
    <mesh geometry={channelGeometry}>
      <meshStandardMaterial
        color={color}
        transparent
        opacity={0.4}
        side={THREE.DoubleSide}
        metalness={0.6}
        roughness={0.4}
      />
      {/* Wireframe overlay for better visualization */}
      <lineSegments>
        <edgesGeometry args={[channelGeometry]} />
        <lineBasicMaterial color={color} linewidth={2} />
      </lineSegments>
    </mesh>
  );
};
